import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { MobergFontFamily } from '../MobergFont/MobergFont'
import { MdWarning, MdCheckCircle } from 'react-icons/md'
import { useBrowserInfo } from '../../Hooks/useBrowserInfo'
import { useDisplayOptimality } from '../../Hooks/useDisplayOptimality'

type RecommendationType = 'success' | 'warning' | 'error'

interface Recommendation {
    type: RecommendationType
    message: string
    suggestion: React.ReactNode
}

export const ZoomResolutionContent: React.FC = () => {
    const [recommendations, setRecommendations] = useState<Recommendation[]>([])
    const browserInfo = useBrowserInfo()
    const { isOptimal } = useDisplayOptimality()

    useEffect(() => {
        if (browserInfo.isSafari) {
            setRecommendations([{
                type: 'error',
                message: 'Safari is not supported',
                suggestion: 'Please use Chrome, Edge, or Firefox for optimal experience.'
            }])
        } else {
            if (!isOptimal) {
                setRecommendations([{
                    type: 'warning',
                    message: 'Display area is too small',
                    suggestion: <span><strong>Zoom out</strong> one step at a time until the display indicator turns green.</span>
                }])
            } else {
                setRecommendations([{
                    type: 'success',
                    message: 'Display settings are optimal',
                    suggestion: 'Your display area is perfect for this application.'
                }])
            }
        }
    }, [browserInfo.isSafari, isOptimal])

    return (
        <Content>
            <RecommendationsSection>
                <RecommendationsList>
                    {recommendations.map((rec, index) => (
                        <RecommendationItem key={index} type={rec.type}>
                            <RecommendationIcon type={rec.type}>
                                {rec.type === 'success' ? <MdCheckCircle size={16} /> : <MdWarning size={16} />}
                            </RecommendationIcon>
                            <RecommendationContent>
                                <RecommendationMessage>{rec.message}</RecommendationMessage>
                                <RecommendationSuggestion>{rec.suggestion}</RecommendationSuggestion>
                            </RecommendationContent>
                        </RecommendationItem>
                    ))}
                </RecommendationsList>
            </RecommendationsSection>

            {!browserInfo.isSafari && (
                <TipSection>
                    <TipContainer>
                        <TipTitle>Keyboard Shortcuts</TipTitle>
                        <TipInstructions>
                            <ShortcutRow>
                                <ShortcutAction>Zoom Out</ShortcutAction>
                                <ShortcutKeysContainer>
                                    <ShortcutKeys>
                                        <PlatformLabel>Windows/Linux</PlatformLabel>
                                        <KeyButton>Ctrl</KeyButton>
                                        <KeyButton>−</KeyButton>
                                    </ShortcutKeys>
                                    <ShortcutKeys>
                                        <PlatformLabel>Mac</PlatformLabel>
                                        <KeyButton>Cmd</KeyButton>
                                        <KeyButton>−</KeyButton>
                                    </ShortcutKeys>
                                </ShortcutKeysContainer>
                            </ShortcutRow>
                            <ShortcutRow>
                                <ShortcutAction>Zoom In</ShortcutAction>
                                <ShortcutKeysContainer>
                                    <ShortcutKeys>
                                        <PlatformLabel>Windows/Linux</PlatformLabel>
                                        <KeyButton>Ctrl</KeyButton>
                                        <KeyButton>+</KeyButton>
                                    </ShortcutKeys>
                                    <ShortcutKeys>
                                        <PlatformLabel>Mac</PlatformLabel>
                                        <KeyButton>Cmd</KeyButton>
                                        <KeyButton>+</KeyButton>
                                    </ShortcutKeys>
                                </ShortcutKeysContainer>
                            </ShortcutRow>
                            <ShortcutRow>
                                <ShortcutAction>Reset to 100%</ShortcutAction>
                                <ShortcutKeysContainer>
                                    <ShortcutKeys>
                                        <PlatformLabel>Windows/Linux</PlatformLabel>
                                        <KeyButton>Ctrl</KeyButton>
                                        <KeyButton>0</KeyButton>
                                    </ShortcutKeys>
                                    <ShortcutKeys>
                                        <PlatformLabel>Mac</PlatformLabel>
                                        <KeyButton>Cmd</KeyButton>
                                        <KeyButton>0</KeyButton>
                                    </ShortcutKeys>
                                </ShortcutKeysContainer>
                            </ShortcutRow>
                        </TipInstructions>
                    </TipContainer>
                </TipSection>
            )}
        </Content>
    )
}

// Styled components (copy from ZoomResolutionChecker.js lines 210-281, 283-362)
const Content = styled.div`
    padding: 24px;
    overflow-y: auto;
    flex: 1;
`

const RecommendationsSection = styled.div`
    margin-bottom: 24px;
`

const RecommendationsList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`

const RecommendationItem = styled.div<{ type: RecommendationType }>`
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 16px;
    background: ${props => {
        if (props.type === 'success') return '#f0fdf4'
        if (props.type === 'error') return '#fef2f2'
        return '#fef3c7'
    }};
    border-radius: 4px;
    border: 1px solid ${props => {
        if (props.type === 'success') return '#bbf7d0'
        if (props.type === 'error') return '#fecaca'
        return '#fde68a'
    }};
`

const RecommendationIcon = styled.div<{ type: RecommendationType }>`
    color: ${props => {
        if (props.type === 'success') return '#059669'
        if (props.type === 'error') return '#dc2626'
        return '#d97706'
    }};
    display: flex;
    align-items: center;
    margin-top: 2px;
`

const RecommendationContent = styled.div`
    flex: 1;
`

const RecommendationMessage = styled.div`
    font-family: ${MobergFontFamily.HEADER};
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 4px;
`

const RecommendationSuggestion = styled.div`
    font-family: ${MobergFontFamily.REGULAR};
    font-size: 13px;
    color: #000000;
    line-height: 1.4;
    
    strong {
        font-weight: 600;
    }
`

const TipSection = styled.div`
    margin-bottom: 24px;
`

const TipContainer = styled.div`
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 4px;
    padding: 24px;
`

const TipTitle = styled.h4`
    font-family: ${MobergFontFamily.HEADER};
    font-size: 14px;
    font-weight: 600;
    color: #374151;
    margin: 0 0 16px 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
`

const TipInstructions = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`

const ShortcutRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 0;
    border-bottom: 1px solid #f1f5f9;
    
    &:last-of-type {
        border-bottom: none;
    }
`

const ShortcutAction = styled.div`
    font-family: ${MobergFontFamily.HEADER};
    font-size: 14px;
    font-weight: 500;
    color: #374151;
`

const ShortcutKeysContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`

const ShortcutKeys = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
`

const PlatformLabel = styled.div`
    font-family: ${MobergFontFamily.REGULAR};
    font-size: 11px;
    color: #6b7280;
    font-weight: 500;
    margin-right: 8px;
    min-width: 80px;
`

const KeyButton = styled.div`
    background: #f1f5f9;
    color: #374151;
    border: 1px solid #e2e8f0;
    border-radius: 4px;
    padding: 6px 10px;
    font-family: ${MobergFontFamily.HEADER};
    font-size: 12px;
    font-weight: 600;
    min-width: 32px;
    text-align: center;
`

export default ZoomResolutionContent